import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import {
  AccountType,
  AddOnRateType,
  AddOnType,
  Driver_Read,
  getV1CompaniesAccountsTypeahead,
  getV1CompaniesCompanyIdDriversTypeahead,
  postV1CompaniesCompanyIdAccountsAccountIdAutoAddOnAssignments,
  postV1CompaniesCompanyIdDriversDriverIdAutoAddOnAssignments,
  RateOwnerType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import { nameIdNonRequiredSchema, NameIdSchemaRequired } from '~constants/regexConst';
import { AccountTypeahead } from '~hooks/useAccount/models';
import { DriverBasic } from '~hooks/useDrivers/models';
import { extractPagination } from '~services/pagination';
import reportRequestError from '~services/reportRequestError';
import { useStores } from '~store/RootStore';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import AddOnHelpLinks from './AddOnHelpLinks';
import AutomaticAddOns, { AutomaticAddOnsHandler } from './AutomaticAddOns';

interface AutomaticSettlementAddOnsDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (ownerId: string, ownerName: string) => void;
  selectedOwnerId: string;
  selectedOwnerName?: string;
  selectedOwnerType: RateOwnerType;
}

const automaticSettlementAddOnsFormValidationSchema = yup.object().shape({
  driver: nameIdNonRequiredSchema.nullable().test(function (value) {
    const { vendor, customer } = this.parent;
    return !!(value || vendor || customer);
  }),
  vendor: nameIdNonRequiredSchema.nullable().test(function (value) {
    const { driver, customer } = this.parent;
    return !!(value || driver || customer);
  }),
  customer: nameIdNonRequiredSchema.nullable().test(function (value) {
    const { driver, vendor } = this.parent;
    return !!(value || driver || vendor);
  }),
});

type AutomaticSettlementAddOnsFormDTO = yup.InferType<
  typeof automaticSettlementAddOnsFormValidationSchema
>;

const AutomaticSettlementAddOnsDialog = ({
  open,
  onClose,
  onSuccess,
  selectedOwnerId,
  selectedOwnerName,
  selectedOwnerType,
}: AutomaticSettlementAddOnsDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const isEdit = Boolean(selectedOwnerId);
  const automaticAddOnsRef = useRef<AutomaticAddOnsHandler>(null);

  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(automaticSettlementAddOnsFormValidationSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      driver: selectedOwnerType === RateOwnerType.DRIVER ? { id: selectedOwnerId } : null,
      vendor: selectedOwnerType === RateOwnerType.VENDOR ? { id: selectedOwnerId } : null,
      customer:
        selectedOwnerType === RateOwnerType.CUSTOMER ? { id: selectedOwnerId } : null,
    },
  });

  useEffect(() => {
    setValue(
      'driver',
      selectedOwnerType === RateOwnerType.DRIVER ? { id: selectedOwnerId } : null,
    );
    setValue(
      'vendor',
      selectedOwnerType === RateOwnerType.VENDOR ? { id: selectedOwnerId } : null,
    );
    setValue(
      'customer',
      selectedOwnerType === RateOwnerType.CUSTOMER ? { id: selectedOwnerId } : null,
    );
  }, [selectedOwnerId, selectedOwnerType]);

  const handleClose = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  const onSubmit = useCallback(() => {
    handleSubmit(async (data: AutomaticSettlementAddOnsFormDTO) => {
      setIsLoading(true);
      try {
        const automaticAddOns = await (automaticAddOnsRef.current?.onSubmit() ??
          Promise.resolve([]));

        if (automaticAddOns.length > 0) {
          const ownerId = data[selectedOwnerType]?.id ?? selectedOwnerId;
          if (!ownerId) return;

          const results = await Promise.allSettled(
            automaticAddOns.map(async (addOn) => {
              const commonBody = {
                name: addOn.name ?? '',
                quantity: addOn.quantity ?? 0,
                revenue_rate_value:
                  addOn.rateType === AddOnRateType.RATE_FOR_EACH ||
                  addOn.rateType === AddOnRateType.PER_LOAD
                    ? addOn.rateValue
                    : undefined,
                percentage:
                  addOn.rateType === AddOnRateType.RATE_PERCENT_OF_TOTAL
                    ? addOn.rateValue
                    : undefined,
                add_on_rate_type: addOn.rateType as AddOnRateType,
                external_id: addOn.id ?? '',
                accounting_id: addOn.accountingId ?? '',
                add_on_id: addOn.id ?? '',
                add_on_types: [AddOnType.SETTLEMENT],
                total_installments: addOn.totalInstallments ?? 0,
                cost_rate_value: addOn.costRateValue ?? undefined,
              };

              if (selectedOwnerType === RateOwnerType.DRIVER) {
                await postV1CompaniesCompanyIdDriversDriverIdAutoAddOnAssignments({
                  path: { 'company-id': companyId, 'driver-id': ownerId },
                  body: commonBody,
                });
              } else {
                await postV1CompaniesCompanyIdAccountsAccountIdAutoAddOnAssignments({
                  path: { 'company-id': companyId, 'account-id': ownerId },
                  body: {
                    ...commonBody,
                    account_type:
                      selectedOwnerType === RateOwnerType.VENDOR
                        ? AccountType.VENDOR
                        : AccountType.CUSTOMER,
                  },
                });
              }
            }),
          );
          const failedResult = results.find((result) => result.status === 'rejected');
          const someAssignmentsWereCreated = results.some(
            (result) => result.status === 'fulfilled',
          );
          if (someAssignmentsWereCreated) {
            onSuccess(ownerId, data[selectedOwnerType]?.name ?? '');
          }
          if (failedResult) {
            throw failedResult.reason;
          }
          handleClose();
        }
      } catch (error) {
        reportRequestError(
          error,
          t('error_messages.auto_add_on_assignments.failed_to_create'),
        );
      } finally {
        setIsLoading(false);
        handleClose();
      }
    })();
  }, [companyId, handleClose, onSuccess, selectedOwnerType]);

  const fetchCustomers = useCallback(
    async (additionalProps: { query?: string; cursor?: string } = {}) => {
      const response = await getV1CompaniesAccountsTypeahead({
        path: { 'company-id': companyId },
        query: {
          'filter[account_types]': [AccountType.CUSTOMER],
          'search[query]': additionalProps.query || undefined,
          'page[limit]': DEFAULT_API_PAGE_SIZE,
          ...(additionalProps.cursor ? { 'page[after]': additionalProps.cursor } : {}),
        },
      });

      const customers = response.data.data.map((customer) => {
        const parsed = AccountTypeahead.parse(customer);

        return { id: parsed.id, name: parsed.name };
      });

      return { data: customers, pagination: extractPagination(response) };
    },
    [companyId],
  );

  const fetchVendors = useCallback(
    async (additionalProps: { query?: string; cursor?: string } = {}) => {
      const response = await getV1CompaniesAccountsTypeahead({
        path: { 'company-id': companyId },
        query: {
          'filter[account_types]': [AccountType.VENDOR],
          'search[query]': additionalProps.query || undefined,
          'page[limit]': DEFAULT_API_PAGE_SIZE,
          ...(additionalProps.cursor ? { 'page[after]': additionalProps.cursor } : {}),
        },
      });

      const vendors = response.data.data.map((vendor) => {
        const parsed = AccountTypeahead.parse(vendor);

        return { id: parsed.id, name: parsed.name };
      });

      return { data: vendors, pagination: extractPagination(response) };
    },
    [companyId],
  );

  const fetchDrivers = useCallback(
    async (additionalProps: { query?: string; cursor?: string } = {}) => {
      const response = await getV1CompaniesCompanyIdDriversTypeahead({
        path: { 'company-id': companyId },
        query: {
          'search[query]': additionalProps.query || undefined,
          'page[limit]': DEFAULT_API_PAGE_SIZE,
          'filter[shared]': false,
          ...(additionalProps.cursor ? { 'page[after]': additionalProps.cursor } : {}),
        },
      });

      const drivers = response.data.data.map((driver) => {
        const parsed = DriverBasic.parse(driver as Driver_Read);

        return { id: parsed.id, name: parsed.fullName };
      });

      return { data: drivers, pagination: extractPagination(response) };
    },
    [companyId],
  );

  const onFormStateChange = useCallback(() => {}, []);

  let dialogTitle = t(
    'administration.automatic_settlement_add_ons.new_settlement_schedule',
  );
  if (isEdit) {
    dialogTitle = selectedOwnerName
      ? t('administration.automatic_settlement_add_ons.add_add_ons_for', {
          name: selectedOwnerName,
        })
      : t('administration.automatic_settlement_add_ons.add_add_on');
  }

  return (
    <Dialog isOpen={open} onClose={handleClose} maxWidth="md">
      <DialogHeader onClose={handleClose}>{dialogTitle}</DialogHeader>

      <DialogContent>
        <Box>
          {!isEdit && (
            <>
              <AutocompleteAsyncFormField
                asyncCallback={
                  selectedOwnerType === RateOwnerType.CUSTOMER
                    ? fetchCustomers
                    : selectedOwnerType === RateOwnerType.VENDOR
                      ? fetchVendors
                      : fetchDrivers
                }
                clearable
                control={control}
                debounceTime={500}
                errors={errors}
                getLabel={(item: NameIdSchemaRequired | null) => item?.name ?? ''}
                getValue={(item: NameIdSchemaRequired | null) => item?.id ?? ''}
                label={`${t(`form_fields.${selectedOwnerType}`)}`}
                name={`${selectedOwnerType}`}
                sx={{ mb: 2 }}
                disablePortal={false}
                isRequired
              />
              <Divider flexItem />
            </>
          )}

          <AutomaticAddOns
            ref={automaticAddOnsRef}
            onFormStateChange={onFormStateChange}
            sx={{ mt: 2 }}
            hasEmptyAddOn
            filteredAddOns={[AddOnType.SETTLEMENT]}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Box sx={{ mr: 'auto' }}>
          <AddOnHelpLinks />
        </Box>

        <SmallButton variant="outlined" color="secondary" onClick={handleClose}>
          {t('actions.cancel')}
        </SmallButton>

        <SmallLoadingButton color="primary" loading={isLoading} onClick={onSubmit}>
          {isEdit ? t('actions.save') : t('actions.create')}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AutomaticSettlementAddOnsDialog;
